/**
 * Form controls on the Square/Mono system.
 *
 * ── Why these exist alongside ui/Input ─────────────────────────────────────
 * `@/components/ui/Input` is the app's ROUNDED/INDIGO lineage — rounded-md,
 * indigo focus ring — and it is widely used. It is not being deleted. But the
 * product surface's destination is Square/Mono, so a field added today should
 * be square, token-driven and mustard-focused like every other control on the
 * page. Migrate a screen's fields when you convert that screen; do not fork.
 *
 * ── Select keeps the native element, and draws its own list ───────────────
 * There are 524 raw `<select>` elements across 246 files, and `<Select>` takes
 * the same <option>/<optgroup> children, so any of them converts by renaming
 * the tag. The <select> stays the control: its label, id, form value, focus
 * and `onChange(e)` are the browser's. What changes is the list it opens. With
 * a mouse or trackpad the system's pull-down is replaced by a list drawn on
 * this system (Wes, 2026-11-03: "a system pull-down, which I don't like"),
 * read from the select's own options each time it opens; a choice sets the
 * select's value and fires a real change event. On a touch screen the phone's
 * own picker opens, as before. `@/components/ui/AppSelect` is the older
 * rounded popover (a handful of consumers); new code uses `Select`.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { FOCUS_RING, LABEL_MONO } from '../mono/primitives';

/** Control heights, matched to BTN_BASE (h-9) so a field and a button placed
    side by side line up without per-site nudging. */
export type ControlSize = 'sm' | 'md';

const SIZE: Record<ControlSize, string> = {
  sm: 'h-8 px-2.5 text-[0.8125rem]',
  md: 'h-9 px-3 text-sm',
};

const CONTROL_BASE =
  `w-full border bg-[var(--surface)] text-[var(--text-pri)] placeholder:text-[var(--text-dim)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${FOCUS_RING}`;

function borderStyle(invalid?: boolean): React.CSSProperties {
  return { borderColor: invalid ? 'var(--coral)' : 'var(--rule-bright)' };
}

/* ── Text input ─────────────────────────────────────────────────────────── */

export interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { size = 'md', invalid, className = '', ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={`${CONTROL_BASE} ${SIZE[size]} ${className}`}
      style={borderStyle(invalid)}
      {...rest}
    />
  );
});

/* ── Textarea ───────────────────────────────────────────────────────────── */

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { rows = 3, invalid, className = '', ...rest },
  ref
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={`${CONTROL_BASE} px-3 py-2 text-sm ${className}`}
      style={borderStyle(invalid)}
      {...rest}
    />
  );
});

/* ── Select ─────────────────────────────────────────────────────────────── */

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
  /** Convenience for flat lists. Ignored when `children` are provided, so
      <optgroup> and custom options keep working. */
  options?: { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
}

/** One row of the open list, read from the select's own <option>s. */
interface ListRow {
  value: string;
  label: string;
  disabled: boolean;
  group: string | null;
}

const LIST_GAP = 4;
const LIST_GUTTER = 16;
const LIST_MAX_HEIGHT = 320;

const ROW_SIZE: Record<ControlSize, string> = {
  sm: 'min-h-8 px-2.5 py-1.5 text-[0.8125rem]',
  md: 'min-h-9 px-3 py-2 text-sm',
};

/** A mouse or trackpad gets the drawn list; a touch screen keeps the phone's picker. */
function drawsOwnList(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches;
}

function readRows(el: HTMLSelectElement): ListRow[] {
  return Array.from(el.options).map((o) => {
    const group = o.parentElement instanceof HTMLOptGroupElement ? o.parentElement : null;
    return { value: o.value, label: o.text, disabled: o.disabled || !!group?.disabled, group: group?.label ?? null };
  });
}

/** Sets the select's value as the browser would, so its onChange receives a real change event. */
function chooseValue(el: HTMLSelectElement, value: string) {
  if (el.value === value) return;
  Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set?.call(el, value);
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

/** The next row a key moves to, skipping rows that cannot be chosen. */
function step(rows: ListRow[], from: number, by: 1 | -1): number {
  for (let i = from + by; i >= 0 && i < rows.length; i += by) if (!rows[i].disabled) return i;
  return from;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', invalid, options, placeholder, className = '', children, onMouseDown, onKeyDown, ...rest },
  ref
) {
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const [rows, setRows] = useState<ListRow[] | null>(null);
  const [current, setCurrent] = useState('');
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<{ top: number; left: number; minWidth: number; maxHeight: number } | null>(null);
  const open = rows !== null;
  const ownList = !rest.multiple;

  const setRefs = useCallback(
    (el: HTMLSelectElement | null) => {
      selectRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref]
  );

  const close = useCallback((refocus: boolean) => {
    setRows(null);
    setPos(null);
    if (refocus) selectRef.current?.focus();
  }, []);

  const openList = () => {
    const el = selectRef.current;
    if (!el || el.disabled) return;
    const next = readRows(el);
    const at = next.findIndex((r) => r.value === el.value);
    setRows(next);
    setCurrent(el.value);
    setActive(at >= 0 ? at : step(next, -1, 1));
  };

  const pick = (i: number) => {
    const el = selectRef.current;
    const row = rows?.[i];
    if (!el || !row || row.disabled) return;
    chooseValue(el, row.value);
    close(true);
  };

  // Below the select when it fits, else above, else on the roomier side with
  // the list scrolling inside — never past the edge of the screen.
  const place = useCallback(() => {
    const el = selectRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const minWidth = Math.min(r.width, vw - LIST_GUTTER * 2);
    const width = Math.max(minWidth, listRef.current?.offsetWidth ?? 0);
    const h = Math.min(listRef.current?.scrollHeight ?? 0, LIST_MAX_HEIGHT);
    const left = Math.max(LIST_GUTTER, Math.min(r.left, vw - width - LIST_GUTTER));
    const below = r.bottom + LIST_GAP;
    const roomBelow = vh - LIST_GUTTER / 2 - below;
    const roomAbove = r.top - LIST_GAP - LIST_GUTTER / 2;
    if (h <= roomBelow || roomBelow >= roomAbove) {
      setPos({ top: below, left, minWidth, maxHeight: Math.min(LIST_MAX_HEIGHT, roomBelow) });
    } else {
      setPos({ top: r.top - LIST_GAP - Math.min(h, roomAbove), left, minWidth, maxHeight: Math.min(LIST_MAX_HEIGHT, roomAbove) });
    }
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // A press outside, a scroll of the page, or Escape closes the list. Escape is
  // caught on window ahead of everything else, so a dialog holding the select
  // stays open.
  useEffect(() => {
    if (!open) return;
    const inside = (t: EventTarget | null) =>
      t instanceof Node && (!!listRef.current?.contains(t) || !!selectRef.current?.contains(t));
    const onPress = (e: PointerEvent) => {
      if (!inside(e.target)) close(false);
    };
    const onScroll = (e: Event) => {
      if (!(e.target instanceof Node && listRef.current?.contains(e.target))) close(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      close(true);
    };
    document.addEventListener('pointerdown', onPress, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    window.addEventListener('keydown', onEscape, true);
    return () => {
      document.removeEventListener('pointerdown', onPress, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('keydown', onEscape, true);
    };
  }, [open, close, place]);

  useEffect(() => {
    if (open && active >= 0) listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  const handleMouseDown = (e: React.MouseEvent<HTMLSelectElement>) => {
    onMouseDown?.(e);
    if (e.defaultPrevented || e.button !== 0 || !ownList || !drawsOwnList()) return;
    e.preventDefault();
    selectRef.current?.focus();
    if (open) close(true);
    else openList();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented || !ownList || !drawsOwnList()) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openList();
      }
      return;
    }
    const list = rows ?? [];
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => step(list, a, e.key === 'ArrowDown' ? 1 : -1));
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      setActive(e.key === 'Home' ? step(list, -1, 1) : step(list, list.length, -1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(active);
    } else if (e.key === 'Tab') {
      close(false);
    } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      // Typing a letter moves to the next row that starts with it.
      e.preventDefault();
      const ch = e.key.toLowerCase();
      for (let k = 1; k <= list.length; k++) {
        const i = (active + k) % list.length;
        if (!list[i].disabled && list[i].label.trim().toLowerCase().startsWith(ch)) {
          setActive(i);
          break;
        }
      }
    }
  };

  return (
    <>
      <select
        ref={setRefs}
        aria-invalid={invalid || undefined}
        aria-expanded={ownList ? open : undefined}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        className={`${CONTROL_BASE} ${SIZE[size]} cursor-pointer appearance-none bg-[length:0.65rem] bg-[right_0.65rem_center] bg-no-repeat pr-8 ${className}`}
        style={{
          ...borderStyle(invalid),
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%238A96AB' stroke-width='1.5'/%3E%3C/svg%3E\")",
        }}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {children ?? options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      {rows &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={rest['aria-label']}
            className="fixed z-[100] overflow-y-auto border bg-[var(--bg-raised)] py-1 text-[var(--text-pri)] shadow-lg"
            style={{
              borderColor: 'var(--rule-bright)',
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              minWidth: pos?.minWidth,
              maxWidth: `calc(100vw - ${LIST_GUTTER * 2}px)`,
              maxHeight: pos?.maxHeight ?? LIST_MAX_HEIGHT,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {rows.map((row, i) => (
              <React.Fragment key={`${row.group ?? ''}:${row.value}:${i}`}>
                {row.group !== null && row.group !== rows[i - 1]?.group && (
                  <li role="presentation" className={`px-3 pb-1 pt-2 ${LABEL_MONO}`}>
                    {row.group}
                  </li>
                )}
                <li
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={row.value === current}
                  aria-disabled={row.disabled || undefined}
                  // Focus stays on the select, so its keyboard and label keep working.
                  onMouseDown={(e) => e.preventDefault()}
                  onPointerEnter={() => !row.disabled && setActive(i)}
                  onClick={() => pick(i)}
                  className={`flex items-center gap-2 ${ROW_SIZE[size]} ${row.group !== null ? 'pl-5' : ''} ${
                    row.disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                  } ${i === active ? 'bg-[var(--surface)]' : ''} ${row.value === '' ? 'text-[var(--text-ter)]' : ''}`}
                >
                  <span className="flex-1">{row.label}</span>
                  {row.value === current && <Check className="h-3.5 w-3.5 flex-shrink-0 text-[var(--mustard)]" aria-hidden />}
                </li>
              </React.Fragment>
            ))}
          </ul>,
          document.body
        )}
    </>
  );
});

/* ── Field wrapper ──────────────────────────────────────────────────────── */

export interface FieldProps {
  label: React.ReactNode;
  /** Explanatory text below the control. Hidden while `error` is present. */
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Marks the control required and draws the marker beside the label. */
  required?: boolean;
  htmlFor?: string;
  /** Label to the left instead of above — for dense settings panels. */
  orientation?: 'vertical' | 'horizontal';
  children: React.ReactNode;
  className?: string;
}

export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  orientation = 'vertical',
  children,
  className = '',
}: FieldProps) {
  const generated = useId();
  const id = htmlFor ?? generated;
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  const control = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        id,
        'aria-describedby': describedBy,
        ...(error ? { invalid: true } : {}),
        ...(required ? { required: true } : {}),
      })
    : children;

  return (
    <div
      className={
        orientation === 'horizontal'
          ? `grid grid-cols-[minmax(0,11rem)_1fr] items-center gap-x-4 gap-y-1 ${className}`
          : `flex flex-col gap-1.5 ${className}`
      }
    >
      <label
        htmlFor={id}
        className="font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]"
      >
        {label}
        {required && <span className="ml-1 text-[var(--coral)]" aria-hidden="true">*</span>}
      </label>
      {control}
      {error ? (
        <p id={`${id}-error`} className="text-xs text-[var(--coral)] col-start-auto">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-[var(--text-dim)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
